'use client';

import React, { useState } from "react"
import { BuyerHomePageBanner, SellerUpdateCard } from "@repo/ui/src/components/buyer"
import { useMeQuery } from "@repo/core/src/services/me"
import { useProPortalInfoQuery } from "@repo/core/src/services/pro-portals"
import { ActionButton, Card, InventoryPreviewTable, Loading, OrderPreviewTable, useLoading } from "@repo/ui"
import { ProPortal } from "@repo/utils/src/types/proPortals"
import { useRouter } from "next/navigation"
import { CompanyUpdate, CompanyUpdateStatus, formatDate, getBusinessDisplayName, getSortAndOrderBy, tableMinHeight } from "@repo/utils"
import { IconArrowRight } from "@tabler/icons-react"
import { useBusinessCompanyUpdates, usePutCompanyUpdateViewMutation } from "@repo/core/src/services/businesses"
import { ViewCompanyUpdateModal } from "@repo/ui/src/components/company-updates"
import { useGetBuyersTopProductsQuery, useOrdersQuery } from "@repo/core/src/services/orders"
import { NavButton } from "@repo/ui/src/components";


const BuyerHome = () => {
	const [selectedCompanyUpdate, setSelectedCompanyUpdate] = useState<CompanyUpdate | null>(null);
	const router = useRouter();

	const { sortBy, orderBy } = getSortAndOrderBy("last_order_desc")
	
	const { data: user, isLoading: userLoading, isError: userIsError, error: userError } = useMeQuery();
	const { data: proPortal, isLoading: proPortalLoading, isError: proPortalIsError, error: proPortalError } = useProPortalInfoQuery();
	const { data: ordersData, isLoading: ordersLoading, isError: ordersIsError, error: ordersError } = useOrdersQuery({ orderBy, sortBy });
	const { data: topProducts, isLoading: topProductsLoading, isError: topProductsIsError, error: topProductsError } = useGetBuyersTopProductsQuery();

	const { mutate } = usePutCompanyUpdateViewMutation();

	const { isLoading: companyUpdatesLoading, data: companyUpdates, isError: companyUpdatesIsError, error: companyUpdatesError } = useBusinessCompanyUpdates({ uuid: proPortal?.business?.uuid ?? ''});

	const {startLoading:startLoadingSpinner} = useLoading();

	const minHeight = tableMinHeight({ table1Items: ordersData?.orders.length ?? 0, table2Items: topProducts?.data.length ?? 0 })

	const updates = companyUpdates ? companyUpdates.companyUpdates.filter((update: CompanyUpdate) => update.status === CompanyUpdateStatus.PUBLISHED) : []

	if(userLoading || proPortalLoading || companyUpdatesLoading || ordersLoading || topProductsLoading) {
		return <Loading type="logo" />;
	}

	if(userIsError || proPortalIsError || ordersIsError || topProductsIsError || companyUpdatesIsError) {
		return <div className="w-full flex flex-wrap p-[15px] md:p-[30px]">
			<div className="w-full flex flex-col items-center justify-center h-screen">
				<p className="text-2xl font-bold">Error loading data</p>
				<p className="text-sm text-gray-500">{userError?.message || proPortalError?.message || ordersError?.message || topProductsError?.message || companyUpdatesError?.message}</p>
			</div>
		</div>;
	}

  return (
    <div className="w-full flex flex-wrap p-[15px] md:p-[30px]">
      
      <BuyerHomePageBanner
        user={user}
        proPortal={proPortal as ProPortal}
        actionButton={
          <ActionButton
            title="Shop Now"
            className="text-white lg:h-[50px] bg-[#EA580C] w-[127px] rounded-3xl lg:text-[20px] inline-flex items-center justify-center whitespace-nowrap"
            handleClick={() => {
							startLoadingSpinner();
							router.push('/shop');
						}}
          />
        }
      />

      {/* <div className="w-full mt-5">
        <BuyerHomePageSetupGuide onClick={() => {}} />
      </div> */}

			{updates && updates.length > 0 && (
				<>
					<div className="w-full py-5">
						<p className="text-[18px] font-semibold">
							Company Updates
						</p>
					</div>

					<div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-[14px]">
						{updates
							.map((companyUpdate: CompanyUpdate) => (
								<SellerUpdateCard
									key={companyUpdate.uuid}
									title={companyUpdate.name}
									content={companyUpdate.message}
									date={formatDate(companyUpdate.updated?.toLocaleString() ?? '')}
									image={companyUpdate.image?.key ?? ''}
									onClick={() => {
										setSelectedCompanyUpdate(companyUpdate)
										mutate({ updateUuid: companyUpdate.uuid })
									}}
								/>
							))}
					</div>
				</>
			)}

			<div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
				<div className="mt-5">
					<OrderPreviewTable
						className="!h-full border-[1px] border-[#D4D4D4] rounded-xl"
						orders={ordersData?.orders ?? []}
						isSeller={false}
						tableHeight={minHeight}
					/>
				</div>
				<div className="mt-2 md:mt-5">
					<InventoryPreviewTable
						className="!h-full border-[1px] border-[#D4D4D4] rounded-xl"
						isBuyer={true}
						inventory={topProducts ?? { data: [] }}
						isLoading={topProductsLoading}
						tableHeight={minHeight}
					/>
				</div>
			</div>

			<div className="w-full py-5">
				<p className="text-[18px] font-semibold">Explore More</p>
			</div>
			<div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 text-[14px]">
				<Card isRounded title="Monitor progress in real time">
					<NavButton href="/orders/all" className="pb-2 text-left md:pb-5 cursor-pointer">
						<p className="text-[14px] mb-5">Track all lumber orders and production stages live, ensuring timely updates and transparency.</p>
						<div className="flex items-center gap-1 text-green-700">
							<p>View</p> <IconArrowRight />
						</div>
					</NavButton>
				</Card>

				<Card isRounded title="Communicate Easily">
					<NavButton href="/chat" className="pb-2 text-left md:pb-5 cursor-pointer">
						<p className="text-[14px] mb-5">
							Send and receive private messages between you and the {proPortal?.business && getBusinessDisplayName(proPortal.business)}.
						</p>
						<div className="flex items-center gap-1 text-green-700">
							<p>View</p> <IconArrowRight />
						</div>
					</NavButton>
				</Card>

				<Card isRounded title="Early Re-order Products">
					<NavButton href="/orders/all" className="pb-2 text-left md:pb-5 cursor-pointer">
						<p className="text-[14px] mb-5">Automatically re-order based on usage to prevent stockouts and ensure continuous supply.</p>
						<div className="flex items-center gap-1 text-green-700">
							<p>View</p> <IconArrowRight />
						</div>
					</NavButton>
				</Card>
			</div>

			{selectedCompanyUpdate && (
				<ViewCompanyUpdateModal
					isOpen={selectedCompanyUpdate ? true : false}
					setIsOpen={() => setSelectedCompanyUpdate(null)}
					companyUpdate={selectedCompanyUpdate}
				/>
			)}
		</div>
	);
};

export default BuyerHome;
